import React, { useEffect, useMemo, useState } from "react";
import { ComponentConfig } from "@measured/puck";

const colorField = (label: string) => ({
  type: "custom" as const,
  label,
  render: ({
    value,
    onChange,
  }: {
    value?: string;
    onChange?: (val: string) => void;
  }) => (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        type="color"
        value={value || "#17265a"}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          width: 44,
          height: 32,
          padding: 0,
          border: "1px solid #e5e7eb",
          borderRadius: 6,
        }}
      />
      <input
        type="text"
        value={value || ""}
        placeholder="#17265a"
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          flex: 1,
          padding: "8px 10px",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          fontFamily: "monospace",
        }}
      />
    </div>
  ),
});

// Spacer Component
export const Spacer: ComponentConfig<{
  height: number;
}> = {
  fields: {
    height: { type: "number", label: "Height (px)", min: 0, max: 500 },
  },
  defaultProps: {
    height: 50,
  },
  render: ({ height }) => {
    return <div style={{ height: `${height}px` }} />;
  },
};

// Text Component
export const Text: ComponentConfig<{
  content: string;
  tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
  fontSize: number;
  fontWeight: number;
  textAlign: "left" | "center" | "right" | "justify";
  color: string;
}> = {
  fields: {
    content: { type: "textarea", label: "Content" },
    tag: {
      type: "select",
      label: "Tag",
      options: [
        { label: "H1", value: "h1" },
        { label: "H2", value: "h2" },
        { label: "H3", value: "h3" },
        { label: "H4", value: "h4" },
        { label: "H5", value: "h5" },
        { label: "H6", value: "h6" },
        { label: "Paragraph", value: "p" },
        { label: "Span", value: "span" },
      ],
    },
    fontSize: { type: "number", label: "Font Size (px)", min: 8, max: 72 },
    fontWeight: {
      type: "number",
      label: "Font Weight",
      min: 100,
      max: 900,
      step: 100,
    },
    textAlign: {
      type: "select",
      label: "Text Align",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
        { label: "Justify", value: "justify" },
      ],
    },
    color: colorField("Text Color"),
  },
  defaultProps: {
    content: "Text content",
    tag: "p",
    fontSize: 16,
    fontWeight: 400,
    textAlign: "left",
    color: "#000000",
  },
  render: ({ content, tag, fontSize, fontWeight, textAlign, color }) => {
    const Tag = tag as keyof JSX.IntrinsicElements;
    return (
      <Tag
        style={{
          fontSize: `${fontSize}px`,
          fontWeight,
          textAlign,
          color,
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 40px",
        }}
      >
        {content}
      </Tag>
    );
  },
};

// Button Component
export const Button: ComponentConfig<{
  text: string;
  link: string;
  variant: "primary" | "secondary" | "outline" | "ghost";
  size: "sm" | "md" | "lg";
  fullWidth: boolean;
  backgroundColor: string;
  textColor: string;
}> = {
  fields: {
    text: { type: "text", label: "Button Text" },
    link: { type: "text", label: "Link" },
    variant: {
      type: "select",
      label: "Variant",
      options: [
        { label: "Primary", value: "primary" },
        { label: "Secondary", value: "secondary" },
        { label: "Outline", value: "outline" },
        { label: "Ghost", value: "ghost" },
      ],
    },
    size: {
      type: "select",
      label: "Size",
      options: [
        { label: "Small", value: "sm" },
        { label: "Medium", value: "md" },
        { label: "Large", value: "lg" },
      ],
    },
    fullWidth: {
      type: "select" as const,
      label: "Full Width",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
  },
  defaultProps: {
    text: "Button",
    link: "#",
    variant: "primary",
    size: "md",
    fullWidth: false,
    backgroundColor: "#17265a",
    textColor: "#ffffff",
  },
  render: ({
    text,
    link,
    variant,
    size,
    fullWidth,
    backgroundColor,
    textColor,
  }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case "primary":
          return {
            backgroundColor: backgroundColor || "#17265a",
            color: textColor || "#ffffff",
            border: "none",
          };
        case "secondary":
          return {
            backgroundColor: "#6b7280",
            color: "#ffffff",
            border: "none",
          };
        case "outline":
          return {
            backgroundColor: "transparent",
            color: backgroundColor || "#17265a",
            border: `2px solid ${backgroundColor || "#17265a"}`,
          };
        case "ghost":
          return {
            backgroundColor: "transparent",
            color: backgroundColor || "#17265a",
            border: "none",
          };
        default:
          return {
            backgroundColor: backgroundColor || "#17265a",
            color: textColor || "#ffffff",
            border: "none",
          };
      }
    };

    const getSizeStyles = () => {
      switch (size) {
        case "sm":
          return { padding: "8px 16px", fontSize: "14px" };
        case "lg":
          return { padding: "16px 32px", fontSize: "18px" };
        default:
          return { padding: "12px 24px", fontSize: "16px" };
      }
    };

    return (
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 40px" }}>
        <a
          href={link}
          style={{
            display: fullWidth ? "block" : "inline-block",
            width: fullWidth ? "100%" : "auto",
            textAlign: "center",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: 600,
            transition: "opacity 0.2s",
            ...getVariantStyles(),
            ...getSizeStyles(),
          }}
        >
          {text}
        </a>
      </div>
    );
  },
};

// Divider Component
export const Divider: ComponentConfig<{
  color: string;
  thickness: number;
  style: "solid" | "dashed" | "dotted";
}> = {
  fields: {
    color: colorField("Color"),
    thickness: { type: "number", label: "Thickness (px)", min: 1, max: 10 },
    style: {
      type: "select",
      label: "Style",
      options: [
        { label: "Solid", value: "solid" },
        { label: "Dashed", value: "dashed" },
        { label: "Dotted", value: "dotted" },
      ],
    },
  },
  defaultProps: {
    color: "#e5e7eb",
    thickness: 1,
    style: "solid",
  },
  render: ({ color, thickness, style }) => {
    return (
      <div
        style={{ maxWidth: "1280px", margin: "40px auto", padding: "0 40px" }}
      >
        <hr
          style={{
            border: "none",
            borderTop: `${thickness}px ${style} ${color}`,
            margin: 0,
          }}
        />
      </div>
    );
  },
};

// Flexible Section / Content Block
export const SectionBlock: ComponentConfig<any> = {
  fields: {
    eyebrow: { type: "text", label: "Eyebrow" },
    heading: { type: "text", label: "Heading" },
    subheading: { type: "textarea", label: "Subheading" },
    body: { type: "textarea", label: "Body" },
    align: {
      type: "select",
      label: "Text Align",
      options: [
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    paddingY: { type: "number", label: "Vertical Padding", min: 0, max: 320 },
    paddingX: { type: "number", label: "Horizontal Padding", min: 0, max: 160 },
    maxWidth: { type: "number", label: "Max Width (px)", min: 320, max: 1920 },
    gap: { type: "number", label: "Stack Gap (px)", min: 0, max: 120 },
    backgroundImage: { type: "text", label: "Background Image URL" },
    overlay: {
      type: "select" as const,
      label: "Show Overlay",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    overlayOpacity: {
      type: "number",
      label: "Overlay Opacity",
      min: 0,
      max: 1,
      step: 0.05,
    },
  },
  defaultProps: {
    eyebrow: "",
    heading: "Your heading",
    subheading: "",
    body: "",
    align: "left",
    backgroundColor: "#ffffff",
    textColor: "#0f172a",
    paddingY: 96,
    paddingX: 32,
    maxWidth: 1200,
    gap: 16,
    overlay: false,
    overlayOpacity: 0.45,
  },
  render: (props: any) => {
    const {
      eyebrow,
      heading,
      subheading,
      body,
      align,
      backgroundColor,
      textColor,
      paddingY,
      paddingX,
      maxWidth,
      gap,
      backgroundImage,
      overlay,
      overlayOpacity,
    } = props;

    const containerStyle: React.CSSProperties = {
      position: "relative",
      backgroundColor,
      color: textColor,
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
      backgroundSize: "cover",
      backgroundPosition: "center",
      padding: `${paddingY}px ${paddingX}px`,
    };

    return (
      <section style={containerStyle}>
        {backgroundImage && overlay && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: `rgba(0,0,0,${overlayOpacity})`,
            }}
          />
        )}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap,
            textAlign: align,
          }}
        >
          {eyebrow && (
            <p
              style={{
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 700,
                fontSize: "12px",
                opacity: 0.8,
              }}
            >
              {eyebrow}
            </p>
          )}
          {heading && (
            <h2 style={{ fontSize: "38px", lineHeight: 1.15, margin: 0 }}>
              {heading}
            </h2>
          )}
          {subheading && (
            <p
              style={{
                fontSize: "18px",
                lineHeight: 1.6,
                margin: 0,
                opacity: 0.9,
              }}
            >
              {subheading}
            </p>
          )}
          {body && (
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                margin: 0,
                opacity: 0.9,
              }}
            >
              {body}
            </p>
          )}
        </div>
      </section>
    );
  },
};

// Media block with flexible alignment
export const MediaBlock: ComponentConfig<any> = {
  fields: {
    imageUrl: { type: "text", label: "Image URL" },
    alt: { type: "text", label: "Alt Text" },
    caption: { type: "textarea", label: "Caption" },
    align: {
      type: "select",
      label: "Alignment",
      options: [
        { label: "Full", value: "full" },
        { label: "Left", value: "left" },
        { label: "Center", value: "center" },
        { label: "Right", value: "right" },
      ],
    },
    maxWidth: { type: "number", label: "Max Width (px)", min: 160, max: 1920 },
    rounded: {
      type: "select" as const,
      label: "Rounded Corners",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    shadow: {
      type: "select" as const,
      label: "Shadow",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    paddingY: { type: "number", label: "Vertical Padding", min: 0, max: 240 },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
  },
  defaultProps: {
    imageUrl:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1400",
    alt: "Image",
    caption: "",
    align: "full",
    maxWidth: 1200,
    rounded: true,
    shadow: true,
    paddingY: 48,
    backgroundColor: "#ffffff",
    textColor: "#0f172a",
  },
  render: (props: any) => {
    const {
      imageUrl,
      alt,
      caption,
      align,
      maxWidth,
      rounded,
      shadow,
      paddingY,
      backgroundColor,
      textColor,
    } = props;

    const justify =
      align === "left"
        ? "flex-start"
        : align === "right"
        ? "flex-end"
        : "center";

    return (
      <section
        style={{
          backgroundColor,
          padding: `${paddingY}px 32px`,
          color: textColor,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: justify,
            width: "100%",
          }}
        >
          <figure
            style={{
              maxWidth,
              width: align === "full" ? "100%" : "auto",
              margin: 0,
            }}
          >
            <img
              src={imageUrl}
              alt={alt}
              style={{
                width: "100%",
                display: "block",
                borderRadius: rounded ? "16px" : "0px",
                boxShadow: shadow ? "0 12px 32px rgba(0,0,0,0.12)" : "none",
                objectFit: "cover",
              }}
            />
            {caption && (
              <figcaption
                style={{ marginTop: "12px", fontSize: "14px", opacity: 0.8 }}
              >
                {caption}
              </figcaption>
            )}
          </figure>
        </div>
      </section>
    );
  },
};

// Feature grid with unlimited items
export const FeatureGrid: ComponentConfig<any> = {
  fields: {
    heading: { type: "text", label: "Heading" },
    description: { type: "textarea", label: "Description" },
    columns: { type: "number", label: "Columns", min: 1, max: 6 },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    cardColor: colorField("Card Background"),
    items: {
      type: "array",
      label: "Features",
      arrayFields: {
        title: { type: "text", label: "Title" },
        body: { type: "textarea", label: "Body" },
        tag: { type: "text", label: "Tag/Badge" },
        icon: { type: "text", label: "Icon / Emoji" },
      },
    } as any,
  },
  defaultProps: {
    heading: "Everything you want to ship",
    description: "Add as many rows as you need. The grid will keep flowing.",
    columns: 3,
    backgroundColor: "#f8fafc",
    textColor: "#0f172a",
    cardColor: "#ffffff",
    items: [
      {
        title: "Unlimited sections",
        body: "Stack sections forever—no limits.",
        tag: "Stack",
        icon: "🧱",
      },
      {
        title: "Custom styling",
        body: "Control colors, spacing, and layout.",
        tag: "Style",
        icon: "🎨",
      },
      {
        title: "Responsive ready",
        body: "Grid collapses gracefully on small screens.",
        tag: "Responsive",
        icon: "📱",
      },
    ],
  },
  render: (props: any) => {
    const FeatureGridComponent: React.FC = () => {
      const {
        heading,
        description,
        columns,
        backgroundColor,
        textColor,
        cardColor,
        items,
      } = props;
      const gridTemplate = useMemo(
        () => `repeat(${columns || 1}, minmax(0, 1fr))`,
        [columns]
      );

      return (
        <section
          style={{ backgroundColor, color: textColor, padding: "80px 32px" }}
        >
          <div
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              gap: 32,
            }}
          >
            <div style={{ maxWidth: 860 }}>
              {heading && (
                <h2 style={{ fontSize: "34px", margin: 0 }}>{heading}</h2>
              )}
              {description && (
                <p
                  style={{ margin: "12px 0 0", opacity: 0.9, lineHeight: 1.5 }}
                >
                  {description}
                </p>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridTemplate,
                gap: 20,
              }}
            >
              {(items || []).map((item: any, idx: number) => (
                <div
                  key={`${item?.title || "item"}-${idx}`}
                  style={{
                    backgroundColor: cardColor,
                    borderRadius: 16,
                    padding: 20,
                    minHeight: 180,
                    boxShadow: "0 10px 32px rgba(15,23,42,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {item?.icon && (
                    <span style={{ fontSize: 20, lineHeight: "20px" }}>
                      {item.icon}
                    </span>
                  )}
                  {item?.tag && (
                    <span
                      style={{
                        alignSelf: "flex-start",
                        backgroundColor: "rgba(23,38,90,0.08)",
                        color: "#17265a",
                        padding: "6px 12px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {item.tag}
                    </span>
                  )}
                  {item?.title && (
                    <h3 style={{ margin: 0, fontSize: 20, lineHeight: 1.3 }}>
                      {item.title}
                    </h3>
                  )}
                  {item?.body && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: 15,
                        lineHeight: 1.6,
                        opacity: 0.9,
                      }}
                    >
                      {item.body}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    };
    return <FeatureGridComponent />;
  },
};

// Simple text box block
export const TextBoxBlock: ComponentConfig<any> = {
  fields: {
    title: { type: "text", label: "Title" },
    body: { type: "textarea", label: "Body" },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    borderColor: colorField("Border Color"),
    padding: { type: "number", label: "Padding (px)", min: 0, max: 120 },
    radius: { type: "number", label: "Corner Radius", min: 0, max: 32 },
  },
  defaultProps: {
    title: "Flexible text box",
    body: "Drop freeform copy here. Great for quick notes, CTAs, or announcements.",
    backgroundColor: "#ffffff",
    textColor: "#0f172a",
    borderColor: "#e5e7eb",
    padding: 20,
    radius: 12,
  },
  render: ({
    title,
    body,
    backgroundColor,
    textColor,
    borderColor,
    padding,
    radius,
  }) => {
    return (
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px" }}>
        <div
          style={{
            backgroundColor,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: radius,
            padding,
            boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
          }}
        >
          {title && (
            <h3 style={{ margin: "0 0 8px", fontSize: 20 }}>{title}</h3>
          )}
          {body && (
            <p style={{ margin: 0, lineHeight: 1.6, opacity: 0.92 }}>{body}</p>
          )}
        </div>
      </div>
    );
  },
};

// Icon grid
export const IconGrid: ComponentConfig<any> = {
  fields: {
    heading: { type: "text", label: "Heading" },
    columns: { type: "number", label: "Columns", min: 1, max: 6 },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    items: {
      type: "array",
      label: "Icons",
      arrayFields: {
        icon: { type: "text", label: "Icon / Emoji" },
        label: { type: "text", label: "Label" },
        body: { type: "textarea", label: "Body" },
      },
    } as any,
  },
  defaultProps: {
    heading: "Icon grid",
    columns: 4,
    backgroundColor: "#ffffff",
    textColor: "#0f172a",
    items: [
      { icon: "⚡️", label: "Speed", body: "Fast setup and editing." },
      { icon: "🎨", label: "Control", body: "Style every block." },
      { icon: "📱", label: "Responsive", body: "Looks great anywhere." },
      { icon: "🔗", label: "Links", body: "Add unlimited nav links." },
    ],
  },
  render: ({ heading, columns, backgroundColor, textColor, items }) => {
    const template = `repeat(${columns || 1}, minmax(0, 1fr))`;
    return (
      <section
        style={{ backgroundColor, color: textColor, padding: "64px 32px" }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {heading && <h2 style={{ margin: 0, fontSize: 28 }}>{heading}</h2>}
          <div
            style={{ display: "grid", gridTemplateColumns: template, gap: 18 }}
          >
            {(items || []).map((item: any, idx: number) => (
              <div
                key={`${item?.label || "icon"}-${idx}`}
                style={{
                  padding: 18,
                  borderRadius: 14,
                  border: "1px solid rgba(15,23,42,0.08)",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {item?.icon && (
                  <span style={{ fontSize: 26 }}>{item.icon}</span>
                )}
                {item?.label && (
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    {item.label}
                  </div>
                )}
                {item?.body && (
                  <div style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>
                    {item.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  },
};

// Fully customizable navbar with unlimited links
export const CustomNavbar: ComponentConfig<any> = {
  fields: {
    brandText: { type: "text", label: "Brand Text" },
    logoUrl: { type: "text", label: "Logo URL" },
    logoUpload: {
      type: "custom" as const,
      label: "Upload Logo",
      render: ({ onChange }) => {
        const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const result = ev.target?.result;
            if (typeof result === "string") {
              onChange?.(result);
            }
          };
          reader.readAsDataURL(file);
        };
        return (
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ width: "100%" }}
          />
        );
      },
    },
    backgroundColor: colorField("Background Color"),
    textColor: colorField("Text Color"),
    accentColor: colorField("Accent Color"),
    loginUrl: { type: "text", label: "Login URL" },
    sticky: {
      type: "select" as const,
      label: "Sticky",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    breakpoint: {
      type: "number",
      label: "Mobile Breakpoint (px)",
      min: 480,
      max: 1280,
    },
    links: {
      type: "array",
      label: "Nav Links",
      arrayFields: {
        label: { type: "text", label: "Label" },
        href: { type: "text", label: "Href" },
        newTab: {
          type: "select" as const,
          label: "Open in new tab",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
      },
    } as any,
  },
  defaultProps: {
    brandText: "Your Brand",
    logoUrl: "",
    logoUpload: "",
    backgroundColor: "#ffffff",
    textColor: "#0f172a",
    accentColor: "#17265a",
    loginUrl: "/login",
    sticky: true,
    breakpoint: 900,
    links: [
      { label: "Home", href: "#" },
      { label: "About", href: "#about" },
      { label: "Events", href: "#events" },
      { label: "Contact", href: "#contact" },
    ],
  },
  render: (props: any) => {
    const CustomNavbarComponent: React.FC = () => {
      const {
        brandText,
        logoUrl,
        logoUpload,
        backgroundColor,
        textColor,
        accentColor,
        loginUrl,
        sticky,
        breakpoint,
        links = [],
      } = props;

      const [isMobile, setIsMobile] = useState<boolean>(
        typeof window !== "undefined"
          ? window.innerWidth < (breakpoint || 900)
          : false
      );
      const [open, setOpen] = useState(false);

      useEffect(() => {
        const handler = () =>
          setIsMobile(window.innerWidth < (breakpoint || 900));
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
      }, [breakpoint]);

      const navLinks = Array.isArray(links) ? links : [];
      const resolvedLogo = logoUpload || logoUrl;

      const containerStyle: React.CSSProperties = {
        position: sticky ? "sticky" : "relative",
        top: 0,
        zIndex: 60,
        width: "100%",
        backgroundColor,
        color: textColor,
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      };

      const linkStyles: React.CSSProperties = {
        color: textColor,
        textDecoration: "none",
        fontWeight: 600,
        padding: "10px 12px",
        borderRadius: 10,
        whiteSpace: "nowrap",
      };

      const loginButton = (
        <a
          href={loginUrl || "/login"}
          style={{
            padding: "10px 18px",
            backgroundColor: accentColor,
            color: "#ffffff",
            borderRadius: 12,
            fontWeight: 700,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
          }}
        >
          Login
        </a>
      );

      return (
        <nav style={containerStyle}>
          <div
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              padding: "14px 24px",
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
              }}
            >
              {resolvedLogo ? (
                <img
                  src={resolvedLogo}
                  alt={brandText || "Logo"}
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              ) : null}
              {brandText && (
                <span
                  style={{
                    fontWeight: 800,
                    fontSize: 18,
                    color: textColor,
                    whiteSpace: "nowrap",
                  }}
                >
                  {brandText}
                </span>
              )}
            </div>

            {!isMobile && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                  overflowX: "auto",
                  padding: "4px 6px",
                  scrollbarWidth: "thin",
                }}
              >
                {navLinks.map((link: any, idx: number) => (
                  <a
                    key={`${link?.href || "link"}-${idx}`}
                    href={link?.href || "#"}
                    style={linkStyles}
                    target={link?.newTab ? "_blank" : "_self"}
                    rel={link?.newTab ? "noopener noreferrer" : undefined}
                  >
                    {link?.label || `Link ${idx + 1}`}
                  </a>
                ))}
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginLeft: "auto",
              }}
            >
              {loginButton}
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setOpen((prev) => !prev)}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    border: "1px solid rgba(15,23,42,0.12)",
                    backgroundColor: "#ffffff",
                    color: textColor,
                    cursor: "pointer",
                  }}
                >
                  {open ? "×" : "☰"}
                </button>
              )}
            </div>
          </div>

          {isMobile && open && (
            <div
              style={{
                borderTop: "1px solid rgba(15,23,42,0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "10px 18px 18px",
              }}
            >
              {navLinks.map((link: any, idx: number) => (
                <a
                  key={`${link?.href || "mobile-link"}-${idx}`}
                  href={link?.href || "#"}
                  target={link?.newTab ? "_blank" : "_self"}
                  rel={link?.newTab ? "noopener noreferrer" : undefined}
                  style={{
                    ...linkStyles,
                    padding: "12px 14px",
                    border: "1px solid rgba(15,23,42,0.08)",
                    borderRadius: 12,
                    backgroundColor: "#ffffff",
                  }}
                  onClick={() => setOpen(false)}
                >
                  {link?.label || `Link ${idx + 1}`}
                </a>
              ))}
            </div>
          )}
        </nav>
      );
    };
    return <CustomNavbarComponent />;
  },
};
